import React, { useCallback, useEffect, useRef } from 'react';
import { AnalyticsContext } from './AnalyticsContext';
import {
  generatePageloadId,
  getAnalyticsWithPageloadId,
  getUtmParameters,
} from '../../analytics/analytics-utils';
import { UtmParameters } from '../../appTypings';
import { Analytics } from '@yleisradio/areena-types';
import { sendPageloadAnalytics } from '../../analytics/sendPageloadAnalytics';
import { useAppFeaturesContext } from '../AppFeaturesContext/AppFeaturesContext';
import { useAuthContext } from '../AuthContext/AuthContext';
import { getTracker } from '../../analytics/snowplow';
import { logger } from '../../../common/logger';
import { addGlobalContexts, setUserId } from '@snowplow/browser-tracker';
import {
  consentContextGenerator,
  userProfileContextGenerator,
} from '../../analytics/snowplowContexts';
import { useLocationWithState } from '../../Routes/useLocationWithState';
export const AnalyticsContextProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { version, device, applicationResolution, mode } =
    useAppFeaturesContext();
  const { user } = useAuthContext();
  const location = useLocationWithState();

  const pageloadIdRef = useRef<string>('');

  useEffect(() => {
    const newPageloadId = generatePageloadId();
    pageloadIdRef.current = newPageloadId;
  }, [location.pathname]);
  const refererRef = useRef<string | undefined>(undefined);
  const utmRef = useRef<UtmParameters>({});

  const sendPageloadAnalyticsCb = useCallback(
    (analytics: Analytics) => {
      const pageloadId = pageloadIdRef.current;
      sendPageloadAnalytics({
        analytics: getAnalyticsWithPageloadId(analytics, pageloadId),
        applicationResolution,
        device,
        loggedInUser: user,
        updateReferer: referer => (refererRef.current = referer),
        clearUtmParameters: () => (utmRef.current = {}),
        version,
        referer: refererRef.current,
        utmParameters: utmRef.current,
      });
    },
    [applicationResolution, device, user, version]
  );

  useEffect(() => {
    const { isSnowplowRunning } = getTracker(mode, device);
    if (isSnowplowRunning) {
      logger.debug('Snowplow is running');

      if (user && user.consent) {
        const version = user.consent.version;
        addGlobalContexts({
          yle_consent: consentContextGenerator({
            version: `v${version?.toString()}`,
            development: user.consent.values.development,
            personalization: user.consent.values.personalization,
            embedded_social_media: user.consent.values.embedded_social_media,
            targeting: user.consent.values.targeting,
          }),
        });

        if (user.transientId) {
          setUserId(user.transientId);

          if (user.isSubAccount) {
            const ageGroup = user.age;
            addGlobalContexts({
              user_profile: userProfileContextGenerator(ageGroup),
            });
          }
        }
      }
    }
  }, [device, mode, user]);

  return (
    <AnalyticsContext.Provider
      value={{
        pageloadId: pageloadIdRef.current,
        setUtmParameters: (mode, isPlayer, id) => {
          utmRef.current = getUtmParameters(mode, isPlayer, id);
        },
        sendPageloadAnalytics: sendPageloadAnalyticsCb,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};
